import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Input } from 'antd';
import chatStore from '../stores/chatStore';
import { useTheme } from '../theme/ThemeContext';

const MODE_TITLES = {
    image: 'Генерация изображений',
    audio: 'Аудио',
    video: 'Генерация видео',
    chat: 'Текстовый чат',
};

const ChatModeIcon = ({ mode }) => {
    const cls = 'shrink-0 opacity-55';
    if (mode === 'image') {
        return (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className={cls} aria-hidden>
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <circle cx="8.5" cy="10" r="1.5" fill="currentColor" />
                <path
                    d="M3.5 16.5l4.5-4.5 3.5 3.5 3-3 6 5.5"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    }
    if (mode === 'audio') {
        return (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className={cls} aria-hidden>
                <path
                    d="M4 10v4M8 7v10M12 4v16M16 8v8M20 11v2"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                />
            </svg>
        );
    }
    if (mode === 'video') {
        return (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className={cls} aria-hidden>
                <rect x="3" y="6" width="13" height="12" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <path
                    d="M16 10.5l5-2.5v8l-5-2.5v-3z"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinejoin="round"
                />
            </svg>
        );
    }
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className={cls} aria-hidden>
            <path
                d="M5 6.5A2.5 2.5 0 017.5 4h9A2.5 2.5 0 0119 6.5v7a2.5 2.5 0 01-2.5 2.5H10l-4 3.5V16H7.5A2.5 2.5 0 015 13.5v-7z"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinejoin="round"
            />
        </svg>
    );
};

const ContextIcon = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        className="shrink-0"
        aria-hidden
    >
        <path
            d="M4 8l8-4 8 4-8 4-8-4z"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinejoin="round"
        />
        <path
            d="M4 12l8 4 8-4"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M4 16l8 4 8-4"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ChatListItem = observer(({ chat }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const active = chatStore.activeChatId === chat.id;
    const [menu, setMenu] = useState(false);
    const menuRef = useRef(null);
    const chatMode = chat.params?.mode || chat.mode || 'chat';
    const contextCount = (chat.context_messages || []).filter(
        (m) => m && String(m.content || '').trim()
    ).length;
    const hasContext = contextCount > 0;

    useEffect(() => {
        if (!menu) return undefined;
        const onDoc = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [menu]);

    const onRename = () => {
        let value = chat.title || '';
        Modal.confirm({
            title: 'Переименовать чат',
            content: (
                <Input
                    defaultValue={value}
                    onChange={(e) => {
                        value = e.target.value;
                    }}
                />
            ),
            okText: 'Сохранить',
            cancelText: 'Отмена',
            onOk: async () => {
                if (value.trim()) await chatStore.renameChat(chat.id, value.trim());
            },
        });
        setMenu(false);
    };

    const onDelete = () => {
        Modal.confirm({
            title: 'Удалить чат?',
            content: 'История сообщений будет удалена безвозвратно.',
            okText: 'Удалить',
            okType: 'danger',
            cancelText: 'Отмена',
            onOk: async () => chatStore.removeChat(chat.id),
        });
        setMenu(false);
    };

    const onTogglePin = async () => {
        setMenu(false);
        await chatStore.togglePinChat(chat.id);
    };

    const onMoveToFolder = async (folderId) => {
        setMenu(false);
        await chatStore.moveChatToFolder(chat.id, folderId);
    };

    return (
        <div
            className={`group relative flex items-center rounded-lg ${
                active
                    ? isDark
                        ? 'bg-white/10'
                        : 'bg-white shadow-sm'
                    : isDark
                      ? 'hover:bg-white/5'
                      : 'hover:bg-black/5'
            }`}
        >
            <button
                type="button"
                className="bananas-list-font flex min-w-0 flex-1 items-center gap-1.5 truncate px-3 py-2 text-left"
                onClick={() => chatStore.selectChat(chat.id)}
            >
                <span className="inline-flex shrink-0" title={MODE_TITLES[chatMode] || MODE_TITLES.chat}>
                    <ChatModeIcon mode={chatMode} />
                </span>
                {hasContext ? (
                    <span
                        className={`inline-flex shrink-0 ${
                            isDark ? 'text-emerald-300/90' : 'text-emerald-700'
                        }`}
                        title={`Задан контекст (${contextCount})`}
                    >
                        <ContextIcon />
                    </span>
                ) : null}
                {chat.is_pinned ? (
                    <span className="shrink-0 opacity-60" title="Закреплён">
                        📌
                    </span>
                ) : null}
                <span className="min-w-0 truncate">{chat.title || 'Новый чат'}</span>
            </button>
            <button
                type="button"
                className={`px-2 opacity-0 group-hover:opacity-100 ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                onClick={() => setMenu((v) => !v)}
            >
                ···
            </button>
            {menu && (
                <div
                    ref={menuRef}
                    className={`absolute right-1 top-9 z-30 max-h-72 w-44 overflow-y-auto rounded-lg border py-1 text-sm shadow-lg ${
                        isDark ? 'border-white/10 bg-[#2a2a2a]' : 'border-black/10 bg-white'
                    }`}
                >
                    <button
                        type="button"
                        className={`block w-full px-3 py-1.5 text-left ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                        onClick={onTogglePin}
                    >
                        {chat.is_pinned ? 'Открепить' : 'Закрепить'}
                    </button>
                    <button
                        type="button"
                        className={`block w-full px-3 py-1.5 text-left ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                        onClick={onRename}
                    >
                        Переименовать
                    </button>
                    <button
                        type="button"
                        className={`block w-full px-3 py-1.5 text-left ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                        onClick={() => {
                            setMenu(false);
                            chatStore.openContextPanel(chat.id);
                        }}
                    >
                        Контекст…
                    </button>
                    <div className={`my-1 border-t ${isDark ? 'border-white/10' : 'border-black/10'}`} />
                    <div className="px-3 py-1 text-[10px] uppercase tracking-wide opacity-45">В папку</div>
                    <button
                        type="button"
                        className={`block w-full px-3 py-1.5 text-left ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'} ${
                            !chat.folder_id ? 'opacity-50' : ''
                        }`}
                        onClick={() => onMoveToFolder(null)}
                        disabled={!chat.folder_id}
                    >
                        Без папки
                    </button>
                    {chatStore.folders.map((folder) => (
                        <button
                            key={folder.id}
                            type="button"
                            className={`block w-full truncate px-3 py-1.5 text-left ${
                                isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
                            } ${chat.folder_id === folder.id ? 'opacity-50' : ''}`}
                            onClick={() => onMoveToFolder(folder.id)}
                            disabled={chat.folder_id === folder.id}
                        >
                            {folder.name}
                        </button>
                    ))}
                    {!chatStore.folders.length && (
                        <div className="px-3 py-1.5 text-xs opacity-45">Нет папок</div>
                    )}
                    <div className={`my-1 border-t ${isDark ? 'border-white/10' : 'border-black/10'}`} />
                    <button
                        type="button"
                        className="block w-full px-3 py-1.5 text-left text-red-500 hover:bg-black/5"
                        onClick={onDelete}
                    >
                        Удалить
                    </button>
                </div>
            )}
        </div>
    );
});

export default ChatListItem;
